import type {
    AgentSubmissionJobRow,
    AgentSubmissions,
    AuditLog,
    CaseImportPreviewResult,
    CaseImportResult,
    CaseRecord,
    CompletedJobRow,
    DuplicateReviewCluster,
    DuplicateReviewClusterPage,
    DuplicateReviewEntityType,
    DuplicateReviewMergeEntityType,
    DuplicateReviewMergeResult,
    FeatureFlag,
    FeatureFlagCreatePayload,
    FeatureFlagUpdatePayload,
    MonitoringPlanResult,
    NotificationSettings,
    Organization,
    OrganizationMember,
    OrgRole,
    OrgRolesList,
    PendingBacklogRow,
    PermissionCatalogResponse,
    PointOfInterest,
    Report,
    ReportMedia,
    ReportingAgent,
    ReportOverview,
    Role,
    SubmissionPeriod,
    Subject,
    SubjectMedia,
    User,
    UserRole,
    Visit,
} from '../types/api';
import { apiList, apiRequest } from './client';

function toQueryString(params: Record<string, string | undefined>): string {
    const search = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== '') {
            search.set(key, value);
        }
    });
    const qs = search.toString();
    return qs ? `?${qs}` : '';
}

export type ListParams = {
    page?: number;
    limit?: number;
    sort?: string;
    [key: string]: string | number | undefined;
};

export const listUsers = (params: ListParams & { email?: string }) => apiList<User>('/users', params);
export const getUser = (id: string) => apiRequest<User>(`/users/${id}`);
export const createPlatformAdmin = (body: {
    email: string;
    firstName: string;
    lastName: string;
    phone: string;
}) =>
    apiRequest<User>('/users/admins', {
        method: 'POST',
        body: JSON.stringify(body),
    });

export const listOrganizations = (params: ListParams) =>
    apiList<Organization>('/organizations', params);
export const getOrganization = (id: string) => apiRequest<Organization>(`/organizations/${id}`);
export const createOrganization = (body: {
    name: string;
    type?: string;
    country?: string;
    settings?: Record<string, unknown>;
}) =>
    apiRequest<Organization>('/organizations', {
        method: 'POST',
        body: JSON.stringify(body),
    });
export const deleteOrganization = (id: string) =>
    apiRequest(`/organizations/${id}`, { method: 'DELETE' });

/**
 * Platform-admin-only. Platform admins are the whole audience of this app, so
 * the UI does not gate these separately — the backend rejects anyone else.
 */
export const getOrganizationNotificationSettings = (organizationId: string) =>
    apiRequest<NotificationSettings>(`/organizations/${organizationId}/notification-settings`);

/** Partial by design: omitted events are left as they are server-side. */
export const updateOrganizationNotificationSettings = (
    organizationId: string,
    patch: Partial<NotificationSettings>,
) =>
    apiRequest<NotificationSettings>(`/organizations/${organizationId}/notification-settings`, {
        method: 'PATCH',
        body: JSON.stringify(patch),
    });

export const listOrganizationMembers = (params: ListParams) =>
    apiList<OrganizationMember>('/organization-members', params);
export const getOrganizationMember = (id: string) =>
    apiRequest<OrganizationMember>(`/organization-members/${id}`);
export const createOrganizationMember = (body: Record<string, unknown>) =>
    apiRequest<OrganizationMember>('/organization-members', {
        method: 'POST',
        body: JSON.stringify(body),
    });
export const deleteOrganizationMember = (id: string) =>
    apiRequest(`/organization-members/${id}`, { method: 'DELETE' });

export const listRoles = (params: ListParams & { scope?: string }) =>
    apiList<Role>('/roles', params);
export const createRole = (body: { name: string; description?: string; scope?: string }) =>
    apiRequest<Role>('/roles', { method: 'POST', body: JSON.stringify(body) });
export const updateRole = (id: string, body: { name?: string; description?: string }) =>
    apiRequest<Role>(`/roles/${id}`, { method: 'PATCH', body: JSON.stringify(body) });
export const deleteRole = (id: string) => apiRequest(`/roles/${id}`, { method: 'DELETE' });

export const getPermissionCatalog = () =>
    apiRequest<PermissionCatalogResponse>('/authorization/permissions');

export const listOrgRoles = (organizationId: string) =>
    apiRequest<OrgRolesList>(`/organizations/${organizationId}/roles`);
export const getOrgRole = (organizationId: string, roleId: string) =>
    apiRequest<OrgRole>(`/organizations/${organizationId}/roles/${roleId}`);
export const createOrgRole = (
    organizationId: string,
    body: { displayName: string; description?: string; permissionKeys: string[] },
) =>
    apiRequest<OrgRole>(`/organizations/${organizationId}/roles`, {
        method: 'POST',
        body: JSON.stringify(body),
    });
export const updateOrgRole = (
    organizationId: string,
    roleId: string,
    body: { displayName?: string; description?: string; permissionKeys?: string[] },
) =>
    apiRequest<OrgRole>(`/organizations/${organizationId}/roles/${roleId}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
    });
export const deleteOrgRole = (organizationId: string, roleId: string) =>
    apiRequest(`/organizations/${organizationId}/roles/${roleId}`, { method: 'DELETE' });

export const assignMemberRole = (memberId: string, roleId: string) =>
    apiRequest<OrganizationMember>(`/organization-members/${memberId}/roles`, {
        method: 'POST',
        body: JSON.stringify({ roleId }),
    });
export const unassignMemberRole = (memberId: string, roleId: string) =>
    apiRequest<OrganizationMember>(`/organization-members/${memberId}/roles/${roleId}`, {
        method: 'DELETE',
    });

export const listUserRoles = (params: ListParams & { scope?: string }) =>
    apiList<UserRole>('/user-role', params);
export const createUserRole = (body: { userId: string; roleId: string }) =>
    apiRequest<UserRole>('/user-role', { method: 'POST', body: JSON.stringify(body) });
export const deleteUserRole = (id: string) =>
    apiRequest(`/user-role/${id}`, { method: 'DELETE' });

export const listAuditLogs = (params: ListParams) => apiList<AuditLog>('/audit-logs', params);

export const listCases = (params: ListParams) => apiList<CaseRecord>('/cases', params);
export const listCasesByOrganization = (organizationId: string, params: ListParams) =>
    apiList<CaseRecord>(`/cases/organization/${organizationId}`, params);
export const listCasesByFieldOfficer = (fieldOfficerId: string, params: ListParams) =>
    apiList<CaseRecord>(`/cases/field-officer/${fieldOfficerId}`, params);
export const getCase = (id: string) => apiRequest<CaseRecord>(`/cases/${id}`);
export const createCase = (body: Record<string, unknown>) =>
    apiRequest<CaseRecord>('/cases', {
        method: 'POST',
        body: JSON.stringify(body),
    });

export const createCaseWithSelfie = (body: Record<string, unknown>, selfie?: File | null) => {
    if (!selfie) {
        return createCase(body);
    }
    const form = new FormData();
    form.append('payload', JSON.stringify(body));
    form.append('subjectSelfie', selfie);
    return apiRequest<CaseRecord>('/cases', {
        method: 'POST',
        body: form,
    });
};
/**
 * Bulk-create cases from a standardized upload file. Sent as multipart so the
 * backend can stream/parse the file; the target org and case type ride along as
 * form fields rather than being inferred per row.
 */
/**
 * First phase of the bulk import: parse the file and classify every row
 * (`create` / `update` / `missing_data` / `parse_error`) without creating anything.
 */
export const previewCaseImport = (body: {
    organizationId: string;
    executionOrganizationId?: string;
    file: File;
}) => {
    const form = new FormData();
    form.append('organizationId', body.organizationId);
    if (body.executionOrganizationId) {
        form.append('executionOrganizationId', body.executionOrganizationId);
    }
    form.append('file', body.file);
    return apiRequest<CaseImportPreviewResult>('/cases/import/preview', {
        method: 'POST',
        body: form,
    });
};

/**
 * Second phase of the bulk import: create cases for the selected row indexes.
 * The same file is re-sent; selected 0-based row indexes come from the preview.
 */
export const importCases = (body: {
    organizationId: string;
    executionOrganizationId?: string;
    caseType: string;
    file: File;
    rows?: number[];
}) => {
    const form = new FormData();
    form.append('organizationId', body.organizationId);
    if (body.executionOrganizationId) {
        form.append('executionOrganizationId', body.executionOrganizationId);
    }
    form.append('caseType', body.caseType);
    form.append('file', body.file);
    if (body.rows && body.rows.length > 0) {
        form.append('rows', JSON.stringify(body.rows));
    }
    return apiRequest<CaseImportResult>('/cases/import', {
        method: 'POST',
        body: form,
    });
};

/**
 * Apply a monitoring plan to many cases at once and generate their visits.
 * `customInterval` is only meaningful when `frequency` is `custom`.
 */
export const applyMonitoringPlan = (body: {
    caseIds: string[];
    enabled: boolean;
    frequency: string;
    customInterval?: { every: number; unit: string };
    startDate?: string;
    endDate?: string;
}) =>
    apiRequest<MonitoringPlanResult>('/cases/monitoring-plan', {
        method: 'POST',
        body: JSON.stringify(body),
    });

export const transitionCaseStatus = (caseId: string, body: { toStatus: string; triggeredBy?: string }) =>
    apiRequest<CaseRecord>(`/cases/${caseId}/status`, {
        method: 'PATCH',
        body: JSON.stringify(body),
    });
export const getMonitoringState = (caseId: string) =>
    apiRequest<import('../types/api').MonitoringStateResponse>(`/cases/${caseId}/monitoring-state`);
export const updateCase = (caseId: string, body: Record<string, unknown>) =>
    apiRequest<CaseRecord>(`/cases/${caseId}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
    });
export const deleteCase = (caseId: string) =>
    apiRequest(`/cases/${caseId}`, { method: 'DELETE' });
export const listVisitsByCase = (caseId: string, params: ListParams) =>
    apiList<Visit>(`/cases/${caseId}/visits`, params);
export const createVisit = (
    caseId: string,
    body: {
        visitType?: string;
        fieldOfficerId?: string;
        status?: string;
        skipAutoVisitPoi?: boolean;
        instructions?: string;
    },
) =>
    apiRequest<Visit>(`/cases/${caseId}/visits`, {
        method: 'POST',
        body: JSON.stringify(body),
    });

export const listVisits = (params: ListParams) => apiList<Visit>('/visits', params);
export const listVisitsByOrganization = (organizationId: string, params: ListParams) =>
    apiList<Visit>(`/visits/organization/${organizationId}`, params);
export const listVisitsByFieldOfficer = (fieldOfficerId: string, params: ListParams) =>
    apiList<Visit>(`/visits/field-officer/${fieldOfficerId}`, params);
export const getVisit = (id: string) => apiRequest<Visit>(`/visits/${id}`);
export const updateVisitData = (
    visitId: string,
    body: {
        instructions?: string;
        department?: string;
        flagged?: boolean;
        flagReason?: string;
        flagDetails?: Record<string, unknown>;
        /** Read back by the field officer app on a rejected visit. */
        rejectionReason?: string;
        rejectionComments?: string;
        /** QA finding recorded when approving a visit. */
        qaAnalysis?: string;
        /** Reviewer notes recorded when approving a visit. */
        approvalNotes?: string;
    },
) =>
    apiRequest<Visit>(`/visits/${visitId}/data`, {
        method: 'PATCH',
        body: JSON.stringify(body),
    });
export const transitionVisitStatus = (visitId: string, body: { toStatus: string }) =>
    apiRequest<Visit>(`/visits/${visitId}/status`, {
        method: 'PATCH',
        body: JSON.stringify(body),
    });
/** Create a standalone address (POI); link it to a visit with linkVisitPoi. */
export const createPoi = (body: {
    poiType: string;
    name?: string;
    claimedAddress?: string;
    claimedLatitude?: number;
    claimedLongitude?: number;
}) =>
    apiRequest<PointOfInterest>('/pois', {
        method: 'POST',
        body: JSON.stringify(body),
    });

/** Point a visit at a specific address (POI) — e.g. carrying one onto a follow-up. */
export const linkVisitPoi = (
    visitId: string,
    body: { poiId: string; verificationScope?: string },
) =>
    apiRequest(`/visits/${visitId}/pois`, {
        method: 'POST',
        body: JSON.stringify(body),
    });

export const assignVisit = (
    visitId: string,
    body: { assignedAgentId: string; reason?: string },
) =>
    apiRequest(`/visits/${visitId}/assign`, {
        method: 'POST',
        body: JSON.stringify(body),
    });

export const unassignVisit = (visitId: string) =>
    apiRequest(`/visits/${visitId}/unassign`, { method: 'POST' });

export const listReports = (params: ListParams) => apiList<Report>('/reports', params);
export const getReport = (id: string) => apiRequest<Report>(`/reports/${id}`);
export const getReportMedia = (reportId: string) =>
    apiRequest<{ data: ReportMedia[]; total: number }>(`/reports/${reportId}/media`);

export const getReportOverview = (params: { organizationId?: string } = {}) =>
    apiRequest<ReportOverview>(`/reporting/overview${toQueryString(params)}`);
export const listCompletedJobs = (params: ListParams) =>
    apiList<CompletedJobRow>('/reporting/completed-jobs', params);
export const listPendingBacklog = (params: ListParams) =>
    apiList<PendingBacklogRow>('/reporting/pending-backlog', params);
export const listReportingAgents = (params: { organizationId?: string } = {}) =>
    apiRequest<{ data: ReportingAgent[]; total: number }>(
        `/reporting/agents${toQueryString(params)}`,
    );
export const getAgentSubmissions = (params: { agentId?: string; organizationId?: string } = {}) =>
    apiRequest<AgentSubmissions>(`/reporting/agent-submissions${toQueryString(params)}`);
export const listAgentSubmissionJobs = (
    params: ListParams & {
        period: SubmissionPeriod;
        agentId?: string;
        organizationId?: string;
    },
) => apiList<AgentSubmissionJobRow>('/reporting/agent-submissions/jobs', params);

export const searchSubjects = (params: ListParams) => apiList<Subject>('/subjects/search', params);
export const getSubject = (id: string) => apiRequest<Subject>(`/subjects/${id}`);
export const updateSubject = (subjectId: string, body: Record<string, unknown>) =>
    apiRequest<Subject>(`/subjects/${subjectId}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
    });
export const uploadSubjectMedia = (
    subjectId: string,
    file: File,
    metadata: { mediaType: string; category: string },
) => {
    const form = new FormData();
    form.append('file', file);
    form.append('mediaType', metadata.mediaType);
    form.append('category', metadata.category);
    return apiRequest<SubjectMedia>(`/subjects/${subjectId}/media/upload`, {
        method: 'POST',
        body: form,
    });
};

export const searchPois = (params: ListParams) => apiList<PointOfInterest>('/pois/search', params);
export const getPoi = (id: string) => apiRequest<PointOfInterest>(`/pois/${id}`);
export const updatePoi = (poiId: string, body: Record<string, unknown>) =>
    apiRequest<PointOfInterest>(`/pois/${poiId}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
    });

export const listDuplicateClusters = (params: {
    entityType: DuplicateReviewEntityType;
    organizationId: string;
    page?: number;
    limit?: number;
}) =>
    apiRequest<DuplicateReviewClusterPage | { data: DuplicateReviewCluster[] } | DuplicateReviewCluster[]>(
        `/duplicate-review/clusters?${new URLSearchParams(
            Object.entries(params).reduce<Record<string, string>>((acc, [key, value]) => {
                if (value !== undefined && value !== '') {
                    acc[key] = String(value);
                }
                return acc;
            }, {}),
        )}`,
    ).then((result) => {
        if (Array.isArray(result)) {
            return {
                data: result,
                total: result.length,
                page: params.page ?? 1,
                limit: params.limit ?? result.length,
                hasMore: false,
            };
        }
        if ('page' in result && 'limit' in result && 'total' in result) {
            return result;
        }
        return {
            data: result.data,
            total: result.data.length,
            page: params.page ?? 1,
            limit: params.limit ?? result.data.length,
            hasMore: false,
        };
    });

export const previewDuplicateMerge = (body: {
    entityType: DuplicateReviewMergeEntityType;
    canonicalId: string;
    duplicateIds: string[];
}) =>
    apiRequest<DuplicateReviewMergeResult>('/duplicate-review/preview', {
        method: 'POST',
        body: JSON.stringify(body),
    });

export const mergeDuplicates = (body: {
    entityType: DuplicateReviewMergeEntityType;
    canonicalId: string;
    duplicateIds: string[];
}) =>
    apiRequest<DuplicateReviewMergeResult>('/duplicate-review/merge', {
        method: 'POST',
        body: JSON.stringify(body),
    });

/** Opt-in feature flags — admin surface for release gates (platform admins only). */
export const listFeatureFlags = () => apiList<FeatureFlag>('/feature-flags', {});
export const createFeatureFlag = (body: FeatureFlagCreatePayload) =>
    apiRequest<FeatureFlag>('/feature-flags', {
        method: 'POST',
        body: JSON.stringify(body),
    });
export const updateFeatureFlag = (id: string, body: FeatureFlagUpdatePayload) =>
    apiRequest<FeatureFlag>(`/feature-flags/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
    });
export const deleteFeatureFlag = (id: string) =>
    apiRequest<{ id: string }>(`/feature-flags/${id}`, { method: 'DELETE' });
